import { useEffect, useState, useCallback } from 'react';
import { FileText, Download, Copy, Send, Plus, Loader2, AlertCircle, Trash, Ban } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { apiService } from '@/services/api';
import { tenantContractsService } from '@/services/tenantContracts.service';
import { TenantContract, ContractStatus, SubscriptionPlan } from '@/types';

interface TenantContractsTabProps {
    tenantId: number;
}

const getStatusBadgeVariant = (status: ContractStatus): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
        case 'pending':
            return 'secondary'; // Yellow/Orange often mapped to secondary or warning if available, using secondary as fallback
        case 'signed':
        case 'active':
            return 'default'; // Green/Primary
        case 'expired':
        case 'cancelled':
            return 'destructive'; // Red
        default:
            return 'outline';
    }
};

const getStatusLabel = (status: ContractStatus): string => {
    switch (status) {
        case 'pending': return 'Aguardando Assinatura';
        case 'signed': return 'Assinado';
        case 'active': return 'Ativo';
        case 'expired': return 'Expirado';
        case 'cancelled': return 'Cancelado';
        default: return status;
    }
}

const formatDate = (isoDate: string | null): string => {
    if (!isoDate) return '-';
    try {
        return new Date(isoDate).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '-';
    }
};

const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(price);
};

export function TenantContractsTab({ tenantId }: TenantContractsTabProps) {
    const [contracts, setContracts] = useState<TenantContract[]>([]);
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [plansLoading, setPlansLoading] = useState<boolean>(true);
    const [plansError, setPlansError] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');

    const hasPendingContract = contracts.some(c => c.status === 'pending');
    const selectedPlan = plans.find(plan => String(plan.id) === selectedPlanId);
    const planStatusMessage = plansLoading ? null : plansError ? 'Erro ao carregar planos.' : plans.length === 0 ? 'Nenhum plano ativo disponível.' : null;
    const createDisabled = actionLoading === 'create' || hasPendingContract || plansLoading || !selectedPlan;
    const planOptions = plansLoading
        ? <option value="">Carregando planos...</option>
        : plans.length === 0
            ? <option value="">Nenhum plano</option>
            : plans.map(plan => (
                <option key={plan.id} value={String(plan.id)}>{plan.name} ({formatPrice(plan.price_monthly)})</option>
            ));

    const fetchContracts = useCallback(async (): Promise<TenantContract[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await tenantContractsService.listContracts(tenantId);
            setContracts(data);
            return data;
        } catch (err: any) {
            const message = err.message || 'Erro ao carregar contratos';
            setError(message);
            toast.error(message);
            return [];
        } finally {
            setLoading(false);
        }
    }, [tenantId]);

    useEffect(() => {
        fetchContracts();
    }, [fetchContracts]);

    useEffect(() => {
        let mounted = true;

        const fetchPlans = async () => {
            setPlansLoading(true);
            setPlansError(false);
            try {
                const response = await apiService.getSubscriptionPlans(false);
                if (!mounted) return;
                setPlans(response.plans);
                setSelectedPlanId((current) => (
                    response.plans.some(plan => String(plan.id) === current)
                        ? current
                        : String(response.plans[0]?.id ?? '')
                ));
            } catch (err: any) {
                if (!mounted) return;
                setPlans([]);
                setPlansError(true);
                setSelectedPlanId('');
                toast.error(err?.message || 'Erro ao carregar planos');
            } finally {
                if (mounted) setPlansLoading(false);
            }
        };

        fetchPlans();

        return () => {
            mounted = false;
        };
    }, []);

    const getSigningUrl = (uuid: string): string => {
        return `${window.location.origin}/contrato/saas/${uuid}`;
    };

    const handleCopyLink = async (contract: TenantContract) => {
        const url = getSigningUrl(contract.uuid);
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Link copiado para a área de transferência!');
        } catch {
            toast.error('Erro ao copiar link');
        }
    };

    const handleResendEmail = async (contract: TenantContract) => {
        setActionLoading(contract.uuid);
        try {
            await tenantContractsService.resendLink(tenantId, contract.uuid);
            toast.success('Link de assinatura reenviado com sucesso!');
        } catch (err: any) {
            toast.error(err.message || 'Erro ao reenviar email');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDownloadPdf = async (contract: TenantContract) => {
        setActionLoading(contract.uuid);
        try {
            await tenantContractsService.downloadAndSavePdf(contract.uuid);
            toast.success('Download iniciado!');
        } catch (err: any) {
            toast.error(err.message || 'Erro ao baixar PDF');
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreateContract = async () => {
        // Evita cliques duplos enquanto a requisição está em andamento
        if (createDisabled || !selectedPlan) return;

        // Força sincronização antes de criar para evitar 409 por contrato pendente já existente
        const latestContracts = await fetchContracts();
        if (latestContracts.some(c => c.status === 'pending')) {
            toast.info('Já existe um contrato pendente. Cancele ou exclua antes de gerar outro.');
            return;
        }
        setActionLoading('create');
        try {
            const response = await tenantContractsService.createContract(tenantId, {
                plan_name: selectedPlan.name,
                monthly_price: formatPrice(selectedPlan.price_monthly),
            });
            toast.success('Contrato criado com sucesso!');

            const newContract: TenantContract = {
                uuid: response.contract.uuid,
                tenant_id: tenantId,
                status: response.contract.status,
                created_at: response.contract.created_at,
                signed_at: null,
                signer_name: null,
                signer_cpf: null,
                signer_ip: null,
                signature_data: null,
                pdf_path: null,
                expires_at: null,
                cancelled_at: null,
                cancel_reason: null,
                signing_url: response.contract.signing_url,
            };
            setContracts((prev) => [newContract, ...prev]);
        } catch (err: any) {
            const message = err?.message || 'Erro ao criar contrato';
            const alreadyExists = message.toLowerCase().includes('já possui contrato') || message.toLowerCase().includes('contract_already_exists');
            if (alreadyExists) {
                toast.info('Já existe um contrato pendente. Atualizando a lista.');
                fetchContracts();
            } else {
                toast.error(message);
            }
        } finally {
            setActionLoading(null);
        }
    };

    const handleCancelContract = async (contract: TenantContract) => {
        if (!window.confirm('Cancelar este contrato agora? A vigência será encerrada.')) return;

        setActionLoading(contract.uuid);
        try {
            await tenantContractsService.cancelContract(tenantId, contract.uuid);
            toast.success('Contrato cancelado com sucesso!');
            setContracts(prev => prev.map(c => c.uuid === contract.uuid ? { ...c, status: 'cancelled' as ContractStatus } : c));
        } catch (err: any) {
            toast.error(err.message || 'Erro ao cancelar contrato');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeleteContract = async (contract: TenantContract) => {
        if (!window.confirm('Excluir este contrato pendente?')) return;

        setActionLoading(contract.uuid);
        try {
            await tenantContractsService.deleteContract(tenantId, contract.uuid);
            toast.success('Contrato excluído com sucesso!');
            setContracts(prev => prev.filter(c => c.uuid !== contract.uuid));
        } catch (err: any) {
            toast.error(err.message || 'Erro ao excluir contrato');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <span className="ml-3 text-gray-500">Carregando contratos...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button variant="ghost" onClick={fetchContracts}>
                    Tentar novamente
                </Button>
            </div>
        );
    }

    if (contracts.length === 0) {
        return (
            <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Nenhum contrato</h3>
                <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                    Este tenant ainda não possui contratos de serviço. Gere um novo contrato para iniciar.
                </p>
                <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="text-sm text-gray-600 font-medium">Plano:</span>
                    <select
                        value={selectedPlanId}
                        onChange={(e) => setSelectedPlanId(e.target.value)}
                        disabled={plansLoading || plans.length === 0}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {planOptions}
                    </select>
                </div>
                {planStatusMessage && (
                    <p className="text-sm text-gray-500 mb-4">{planStatusMessage}</p>
                )}
                <Button onClick={handleCreateContract} disabled={createDisabled}>
                    {actionLoading === 'create' ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Gerando...
                        </>
                    ) : (
                        'Gerar Contrato'
                    )}
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Contratos ({contracts.length})
                </h3>
                <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-600">Plano:</span>
                            <select
                                value={selectedPlanId}
                                onChange={(e) => setSelectedPlanId(e.target.value)}
                                disabled={plansLoading || plans.length === 0}
                                className="border border-gray-300 rounded-md px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {planOptions}
                            </select>
                        </div>
                        <Button
                            size="sm"
                            onClick={handleCreateContract}
                            disabled={createDisabled}
                            className="gap-2"
                        >
                            {actionLoading === 'create' ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Plus className="w-4 h-4" />
                            )}
                            Novo Contrato
                        </Button>
                    </div>
                    {planStatusMessage && (
                        <p className="text-xs text-gray-500">{planStatusMessage}</p>
                    )}
                </div>
            </div>

            {contracts.map((contract) => {
                const isPending = contract.status === 'pending';
                const canDownload = contract.status === 'signed' || contract.status === 'active';
                const isLoading = actionLoading === contract.uuid;

                return (
                    <Card key={contract.uuid} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-3">
                                    <Badge variant={getStatusBadgeVariant(contract.status)}>
                                        {getStatusLabel(contract.status)}
                                    </Badge>
                                    <span className="text-xs text-gray-500 font-mono">
                                        {contract.uuid.slice(0, 8)}...
                                    </span>
                                </div>

                                <div className="text-sm text-gray-600 space-y-1">
                                    <p>
                                        <span className="font-medium">Criado em:</span> {formatDate(contract.created_at)}
                                    </p>
                                    {contract.signed_at && (
                                        <p>
                                            <span className="font-medium">Assinado em:</span> {formatDate(contract.signed_at)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-wrap">
                                {isPending && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteContract(contract)}
                                            disabled={isLoading}
                                            className="gap-2 text-red-600 hover:text-red-700"
                                        >
                                            <Trash className="w-4 h-4" />
                                            Excluir
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleCopyLink(contract)}
                                            disabled={isLoading}
                                            className="gap-2"
                                        >
                                            <Copy className="w-4 h-4" />
                                            Copiar Link
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleResendEmail(contract)}
                                            disabled={isLoading}
                                            className="gap-2"
                                        >
                                            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                            Reenviar Email
                                        </Button>
                                    </>
                                )}

                                {canDownload && (
                                    <>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={() => handleDownloadPdf(contract)}
                                            disabled={isLoading}
                                            className="gap-2"
                                        >
                                            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                            Baixar PDF
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleCancelContract(contract)}
                                            disabled={isLoading}
                                            className="gap-2 text-red-600 hover:text-red-700"
                                        >
                                            <Ban className="w-4 h-4" />
                                            Cancelar
                                        </Button>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}
